import { useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';

interface IgnoreBannerReturn {
  ignoreBanner: {
    isVisible: boolean;
    message: string;
    colorTheme: 'Success' | 'Critical';
    onClose: () => void;
  };
}

/**
 * Custom hook for managing ignore rule banners
 * Checks sessionStorage for ignore success/error data and manages banner state
 */
const useIgnoreBanner = (): IgnoreBannerReturn => {
  const [showIgnoreBanner, setShowIgnoreBanner] = useState(false);
  const [ignoreMessage, setIgnoreMessage] = useState('');
  const [ignoreColorTheme, setIgnoreColorTheme] = useState<
    'Success' | 'Critical'
  >('Success');

  // Check for rule ignore data from sessionStorage
  useEffect(() => {
    try {
      // Check for success data
      const successData = sessionStorage.getItem('ruleIgnoreSuccess');

      if (successData) {
        const parsed = JSON.parse(successData) as {
          timestamp: number;
        };
        const { timestamp } = parsed;

        // only show if recent (prevent stale data from prior crashes)
        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setIgnoreMessage('The rule recommendation was successfully ignored.');
          setIgnoreColorTheme('Success');
          setShowIgnoreBanner(true);
        }

        sessionStorage.removeItem('ruleIgnoreSuccess'); // always clean up
      }

      // Check for error data
      const errorData = sessionStorage.getItem('ruleIgnoreError');

      if (errorData) {
        const parsed = JSON.parse(errorData) as {
          timestamp: number;
        };
        const { timestamp } = parsed;

        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setIgnoreMessage(
            'The rule could not be ignored due to an internal error.'
          );
          setIgnoreColorTheme('Critical');
          setShowIgnoreBanner(true);
        }

        sessionStorage.removeItem('ruleIgnoreError');
      }
    } catch (parseError) {
      Logger.error('Error processing ignore rule recommendation:', parseError);
      // clean up corrupted data
      sessionStorage.removeItem('ruleIgnoreSuccess');
      sessionStorage.removeItem('ruleIgnoreError');
    }
  }, []);

  return {
    ignoreBanner: {
      isVisible: showIgnoreBanner,
      message: ignoreMessage,
      colorTheme: ignoreColorTheme,
      onClose: () => setShowIgnoreBanner(false),
    },
  };
};

export default useIgnoreBanner;
